import { useCallback, useEffect, useState, useRef } from 'react';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { useAuth0Token } from './useAuth0Token';

export const useReferencedComponent = (documentId: string) => {
  const [component, setComponent] = useState<any>(null);
  const { simpleToken } = useAuth0Token();
  const hasFetchedRef = useRef(false);
  const hasFetchedWithTokenRef = useRef(false);
  const isLoadingRef = useRef(false);
  const isLoadingWithTokenRef = useRef(false);

  const fetchReferencedComponent = useCallback(
    async (withToken: boolean) => {
      // Prevent duplicate fetches
      if (withToken) {
        if (isLoadingWithTokenRef.current || hasFetchedWithTokenRef.current) {
          return;
        }
        isLoadingWithTokenRef.current = true;
      } else {
        if (isLoadingRef.current || hasFetchedRef.current) {
          return;
        }
        isLoadingRef.current = true;
      }

      try {
        const res = await apiClient.get(ApiType.apimNafNoApi, {
          url: ApiPaths[ApiType.apimNafNoApi].component.get(documentId),
        });
        setComponent(res.data);
        hasFetchedRef.current = true;
        if (withToken) {
          hasFetchedWithTokenRef.current = true;
          isLoadingWithTokenRef.current = false;
        } else {
          isLoadingRef.current = false;
        }
      } catch (e) {
        // Reset loading state on error
        if (withToken) {
          isLoadingWithTokenRef.current = false;
        } else {
          isLoadingRef.current = false;
        }
        // TODO: handle error?
      }
    },
    [documentId],
  );

  // Fetch immediately on mount (don't wait for token)
  useEffect(() => {
    if (!hasFetchedRef.current && !isLoadingRef.current) {
      fetchReferencedComponent(false).then();
    }
  }, [documentId, fetchReferencedComponent]);

  // Re-fetch when token becomes available to get updated data
  useEffect(() => {
    if (simpleToken && !hasFetchedWithTokenRef.current && !isLoadingWithTokenRef.current) {
      fetchReferencedComponent(true).then();
    }
  }, [simpleToken, fetchReferencedComponent, documentId]);

  return component;
};
